/**
 * Design System - Base Avatar subcomponent
 * https://mercury.design/system?p=avatar
 * https://www.figma.com/file/S7Q78jkg0O92CodjYielQO/%F0%9F%92%BB-Design-System---Web?node-id=133%3A4762
 */

import classnames from 'classnames'
import React from 'react'
import {type OmitStrict} from 'type-zoo'
import HoverTrackingDiv, {
  type HoverTrackingDivProps,
} from '~/mercuryWebCompat/_components/UtilityDivs/HoverTrackingDiv'
import styles from '~/mercuryWebCompat/design-system/MWDSAvatar/styles.module.css'

export type AvatarSize = 'small' | 'large' | 'custom'

type BaseAvatarInternalProps = {
  children: (hovered: boolean) => React.ReactNode
}

// TODO (DS): Some of these props have little effect on AvatarImage! e.g. `noHover` and `forceHover`
/** external props that all avatars accept as props themselves */
export type BaseAvatarExternalProps = {
  /**
   * Size of the Avatar. Small is `28px`, large is `40px`.
   * If `'custom'` is selected, the default sizing styles are removed, so that you can size it however you need to.
   */
  size?: AvatarSize

  /**
   * A `ref` to attach to the outer HoverTrackingDiv wrapping the Avatar.
   */
  divRef?: React.Ref<HTMLDivElement>

  /**
   * Set this to force-enable the hover state (i.e. if the parent is hovered and this should look hovered)
   */
  forceHover?: boolean

  /**
   * Set this to remove the hover state (slightly darker color) for the avatar.
   */
  noHover?: boolean

  /**
   * By default, the Avatar is a circle. If set, this prop makes the Avatar a rounded square instead.
   * Square sizes may be slightly smaller.
   *
   * @deprecated
   */
  square?: boolean
} & OmitStrict<React.HTMLAttributes<HTMLDivElement>, 'children'> &
  Pick<HoverTrackingDivProps, 'hoveredClassName'>

// TODO (DS): Most of the uses of `style` appear unnecessary, and should be fixed in future.
/**
 * The base component used by all Avatar components/subcomponents.
 * It primarily handles the styling of the Avatar's parent wrapper.
 */
const BaseAvatar = ({
  children,
  className,
  forceHover,
  noHover,
  size = 'small',
  square,
  ...divProps
}: BaseAvatarInternalProps & BaseAvatarExternalProps) => {
  return (
    <HoverTrackingDiv
      {...divProps}
      className={classnames(
        /** if we have indicated the size of the Avatar will be custom we do not apply a standard size */
        size !== 'custom' && styles[size],
        !square ? styles.circle : styles.square,
        noHover && styles.noHover,
        forceHover && styles.forceHover,
        className
      )}
    >
      {children}
    </HoverTrackingDiv>
  )
}

export default BaseAvatar
